import type { DateTime } from "luxon";
import { getStaffingDate, getStaffingDayStart } from "./dateUtils";
import {
	ChangesSource,
	type HppdPostNumbers,
	type Nurse,
	type OriginPerson,
	type RatioPostNumbers,
	RequestOriginKind,
	RequestResponsesStatusOptions,
	type ShiftRequest,
	StatsMode,
	type TimeOffCandidate,
} from "./models";
import { requestTakesAnyRole } from "./roles";
import { deterministicPercent, hashString } from "./utilFunctions";

/**
 * Demo-only seeding primitives for `demoRequestOrigins.ts`: deterministic picks keyed on the request id, so the same
 * request always tells the same story. Only `inhouse\d*__` demo departments use them; the API answers those 404.
 */
export type DemoOriginsContext = {
	/** Schedulable nurses of the department, sorted by id so a seeded pick never depends on load order. */
	roster: readonly Nurse[];
	/** The signed-in user, credited only for what their own user id created. */
	currentUser: { userId: string; person: OriginPerson } | null;
	/** Locations with a ratio ladder; empty in a department that does not staff by ratio. */
	ratioLocations: readonly string[];
	timezone: string;
	/** The department's day start and scheduled grace: a demo time off spans schedule days, as a real one does. */
	dayStart: string;
	graceMinutes: number;
	now: DateTime;
};

export const PERCENT_SCALE = 100;
/** A seeded yes/no splits the requests evenly. */
export const COIN_FLIP_PERCENT = 50;

/**
 * The cause a posted request is given when its real fields do not name one: auto-posts lead, the way a demo
 * department's list reads once InHouse is doing the posting. Weights add up to `PERCENT_SCALE`.
 */
export const SEEDED_KIND_WEIGHTS: readonly (readonly [RequestOriginKind, number])[] = [
	[RequestOriginKind.AutoPost, 50],
	[RequestOriginKind.PostedByPerson, 12],
	[RequestOriginKind.TimeOff, 16],
	[RequestOriginKind.PostedByAnnie, 8],
	[RequestOriginKind.Deactivation, 7],
	[RequestOriginKind.FromRecommendation, 7],
];

/** How a demo call-off reached the schedule: the app, the support line, or an admin. */
export const CALL_OFF_CHANNELS: readonly ChangesSource[] = [
	ChangesSource.NurseApp,
	ChangesSource.SupportLinePhone,
	ChangesSource.SupportLineSms,
	ChangesSource.Staffing,
	ChangesSource.Assistant,
];

export const keyedPercent = (key: string, salt: string): number => deterministicPercent(`${key}:${salt}`);
export const keyedBetween = (key: string, salt: string, min: number, max: number): number => min + (keyedPercent(key, salt) / PERCENT_SCALE) * (max - min);
const keyedInt = (key: string, salt: string, range: { MIN: number; MAX: number }): number =>
	range.MIN + Math.floor((keyedPercent(key, salt) / PERCENT_SCALE) * (range.MAX - range.MIN + 1));

export const percent = (request: ShiftRequest, salt: string): number => keyedPercent(request.id, salt);

export const between = (request: ShiftRequest, salt: string, min: number, max: number): number => keyedBetween(request.id, salt, min, max);

export const seededIndex = (length: number, seed: string): number => Math.abs(hashString(seed)) % length;

export const pick = <T>(items: readonly T[], seed: string): T | null => (items.length === 0 ? null : items[seededIndex(items.length, seed)]);

export const seededInt = (request: ShiftRequest, salt: string, range: { MIN: number; MAX: number }): number => keyedInt(request.id, salt, range);

export const weightedKind = (request: ShiftRequest, salt: string): RequestOriginKind => {
	const value = percent(request, salt);
	let ceiling = 0;
	for (const [kind, weight] of SEEDED_KIND_WEIGHTS) {
		ceiling += weight;
		if (value < ceiling) return kind;
	}
	throw new Error(`SEEDED_KIND_WEIGHTS must add up to ${PERCENT_SCALE}; ${value} fell past ${ceiling}`);
};

export const leadMinutesOf = (request: ShiftRequest, at: DateTime): number => Math.round(request.message.shift_start.diff(at, "minutes").minutes);

export const toPerson = (nurse: Nurse): OriginPerson => ({ nurse_id: nurse.id, first_name: nurse.firstName, last_name: nurse.lastName });

export const agencyNameOf = (request: ShiftRequest): string | null =>
	"agency_source_name" in request && typeof request.agency_source_name === "string" && request.agency_source_name !== "" ? request.agency_source_name : null;

/** The approved answers: each filled one slot. `needed_nurses` is what is still open, so the two add up to all opened. */
export const approvedNurseIdsOf = (request: ShiftRequest): number[] =>
	request.message.request_data.filter((answer) => answer.status === RequestResponsesStatusOptions.APPROVED).map((answer) => answer.nurse_id);

const fitsRequest = (nurse: Nurse, request: ShiftRequest): boolean =>
	request.job_id !== undefined ? nurse.jobIds?.includes(request.job_id) === true : requestTakesAnyRole(request.message.roles, [nurse.role]);

export const pickRosterNurse = (request: ShiftRequest, salt: string, ctx: DemoOriginsContext, excludeId: number | null): Nurse | null => {
	const pool = ctx.roster.filter((nurse) => nurse.id !== excludeId);
	const fitting = pool.filter((nurse) => fitsRequest(nurse, request));
	return pick(fitting, `${request.id}:${salt}`);
};

export const rosterNurseById = (ctx: DemoOriginsContext, nurseId: number): Nurse | null => ctx.roster.find((nurse) => nurse.id === nurseId) ?? null;

/** Whoever the request's creator is, when the demo can say: a roster nurse, or the signed-in user if it was them. */
export const creatorOf = (request: ShiftRequest, ctx: DemoOriginsContext): OriginPerson | null => {
	const nurse = ctx.roster.find((candidate) => candidate.userId === request.created_by);
	if (nurse !== undefined) return toPerson(nurse);
	return ctx.currentUser !== null && ctx.currentUser.userId === request.created_by ? ctx.currentUser.person : null;
};

/** Who posted it. A post is a scheduler's act, so an unknown creator is told as the signed-in scheduler's. */
export const posterOf = (request: ShiftRequest, ctx: DemoOriginsContext): OriginPerson | null => {
	const creator = creatorOf(request, ctx);
	if (creator !== null) return creator;
	// DEFAULT: demo only — the seeder's requests carry a system creator; the signed-in scheduler stands in as poster.
	return ctx.currentUser === null ? null : ctx.currentUser.person;
};

const HPPD_CENSUS = { MIN: 55, MAX: 90 } as const;
const HPPD_TARGET = { MIN: 3.2, MAX: 3.6 } as const;
const HPPD_SHORTFALL = { MIN: 0.25, MAX: 0.5 } as const;
const POSTING_THRESHOLD_PERCENT = 95;
const POSTED_HOURS = { MIN: 0, MAX: 24 } as const;
const HPPD_DECIMALS = 2;
const RATIO_REQUIRED = { MIN: 4, MAX: 7 } as const;
const RATIO_SCHEDULED_SHORT_BY = 2;
const RATIO_ALREADY_POSTED = 1;
const TIME_OFF_DAYS_BEFORE = { MIN: 0, MAX: 1 } as const;
const TIME_OFF_DAYS_AFTER = { MIN: 0, MAX: 2 } as const;

const toDecimals = (value: number): number => Number(value.toFixed(HPPD_DECIMALS));

/** The day a demo post's numbers are for: the shift start's calendar date, the date recommendations build shifts on. */
export const recommendationDayOf = (request: ShiftRequest, ctx: DemoOriginsContext): DateTime =>
	request.message.shift_start.setZone(ctx.timezone).startOf("day");

/** The schedule day a demo request's shift belongs to, by the department's day start. */
const scheduleDayOf = (request: ShiftRequest, ctx: DemoOriginsContext): DateTime =>
	getStaffingDate(request.message.shift_start.setZone(ctx.timezone), ctx.dayStart, ctx.graceMinutes).startOf("day");

/** A day below its posting threshold, seeded from `key`: hours scheduled and posted under what the threshold needs at that census. */
export const hppdNumbers = (key: string, staffingDate: DateTime): HppdPostNumbers => {
	const census = keyedInt(key, "census", HPPD_CENSUS);
	const target = toDecimals(keyedBetween(key, "target", HPPD_TARGET.MIN, HPPD_TARGET.MAX));
	const before = toDecimals(target - keyedBetween(key, "gap", HPPD_SHORTFALL.MIN, HPPD_SHORTFALL.MAX));
	const thresholdHppd = toDecimals((target * POSTING_THRESHOLD_PERCENT) / PERCENT_SCALE);
	const postedHours = keyedInt(key, "posted-hours", POSTED_HOURS);
	const neededHours = Math.round(thresholdHppd * census);
	const haveHours = Math.round(before * census);
	return {
		kind: StatsMode.PPD_HPPD,
		staffing_date: staffingDate,
		hppd_before_post: before,
		hppd_target: target,
		threshold_percent: POSTING_THRESHOLD_PERCENT,
		threshold_hppd: thresholdHppd,
		census,
		census_by_shift: {},
		scheduled_hours: haveHours - postedHours,
		posted_hours: postedHours,
		needed_hours: neededHours,
		hours_short: Math.max(0, neededHours - haveHours),
	};
};

/** A ratio story names one more post, so it is only told for a cause that opened exactly one slot. */
const ratioNumbers = (request: ShiftRequest, ctx: DemoOriginsContext): RatioPostNumbers | null => {
	const location =
		request.location !== undefined && ctx.ratioLocations.includes(request.location)
			? request.location
			: pick(ctx.ratioLocations, `${request.id}:ratio-location`);
	if (location === null) return null;
	const required = seededInt(request, "required", RATIO_REQUIRED);
	const scheduled = required - RATIO_SCHEDULED_SHORT_BY;
	return {
		kind: StatsMode.Ratio,
		staffing_date: recommendationDayOf(request, ctx),
		census: seededInt(request, "census", HPPD_CENSUS),
		role_group: request.message.roles.join(", "),
		job_id: request.job_id !== undefined ? request.job_id : null,
		location,
		required,
		scheduled,
		posted: RATIO_ALREADY_POSTED,
		deficit: required - scheduled - RATIO_ALREADY_POSTED,
	};
};

export const autoPostNumbers = (request: ShiftRequest, ctx: DemoOriginsContext, allowRatio: boolean): HppdPostNumbers | RatioPostNumbers => {
	const ratio = allowRatio && percent(request, "rationale") < COIN_FLIP_PERCENT ? ratioNumbers(request, ctx) : null;
	return ratio !== null ? ratio : hppdNumbers(request.id, recommendationDayOf(request, ctx));
};

/** Days off that always include the shift's own day, the way a real time-off approval reopens it. */
export const timeOffCandidate = (request: ShiftRequest, nurse: OriginPerson, ctx: DemoOriginsContext): TimeOffCandidate => {
	const shiftDay = scheduleDayOf(request, ctx);
	// Saved day start to day start, like a real one: it ends where the day after its last day starts.
	return {
		nurse,
		starts_at: getStaffingDayStart(shiftDay.minus({ days: seededInt(request, "pto-before", TIME_OFF_DAYS_BEFORE) }), ctx.dayStart),
		ends_at: getStaffingDayStart(shiftDay.plus({ days: seededInt(request, "pto-after", TIME_OFF_DAYS_AFTER) + 1 }), ctx.dayStart),
	};
};
